import * as Logger from 'OK/logger';

/**
 * Класс для отправки данных в систему статистики.
 */
export class StatLogger {
    static SEEN_SUCCESS = 'seen';

    /**
     * Преобразует значение в строку.
     * Для объектных значений используется `JSON.stringify`, в остальных случаях - `String`.
     *
     * @param value
     *      Преобразуемое значение.
     * @return
     *      Результат преобразования значения в строку.
     */
    static valueToStr(value: unknown): string {
        if (value && typeof value === 'object' && !(value instanceof Error)) {
            return JSON.stringify(value);
        }
        return String(value);
    }

    /**
     * Преобразует параметры в строки с помощью метода {@link #valueToStr}.
     *
     * Важно! Значения `null` и `undefined` игнорируются и не включаются в результирующий список.
     * Для набора параметров `[1, false, null, 'val', undefined]` будет возвращен массив `['1', 'false', 'val']`.
     *
     * @param params
     *      Набор преобразуемых параметров.
     * @return
     *      Массив параметров, преобразованных в строки, за исключением значений `null` и `undefined`.
     */
    static getStatParams(...params: unknown[]): string[] {
        return params.reduce((list: string[], item: unknown) => {
            // Ignore null and undefined
            if (item != null) {
                list.push(StatLogger.valueToStr(item));
            }
            return list;
        }, []) as string[];
    }

    static success(operation: string, ...args: string[]) {
        Logger.success(operation, ...args);
    }

    static error(place: string, error?: unknown) {
        const errorValue = error
            ? StatLogger.valueToStr(error)
            : '';
        Logger.error(place, errorValue);
    }

    static force(operation: string, param1?: string, param2?: string) {
        Logger.force(operation, param1, param2);
    }

    static duration(operation: string, duration: number, ...args: string[]) {
        Logger.duration(operation, duration, ...args);
    }

    static seen(...args: string[]) {
        StatLogger.success(StatLogger.SEEN_SUCCESS, ...args);
    }

    static clob(operation: string, clob: string, ...args: string[]) {
        Logger.clob(operation, clob, ...args);
    }

    statPrefix: string;

    constructor(statPrefix?: string) {
        this.statPrefix = statPrefix || '';
    }

    getStatOperation(operation: string) {
        return `${this.statPrefix}${operation}`;
    }

    success(operation: string, param1?: unknown, param2?: unknown) {
        StatLogger.success(this.getStatOperation(operation), ...StatLogger.getStatParams(param1, param2));
    }

    error(place: string, error?: unknown) {
        StatLogger.error(this.getStatOperation(place), error);
    }

    force(operation: string, param1?: unknown, param2?: unknown) {
        StatLogger.force(this.getStatOperation(operation), ...StatLogger.getStatParams(param1, param2));
    }

    duration(operation: string, duration: number, param1?: unknown, param2?: unknown) {
        StatLogger.duration(this.getStatOperation(operation), duration, ...StatLogger.getStatParams(param1, param2));
    }

    seen(param1: unknown, param2?: unknown) {
        StatLogger.seen(...StatLogger.getStatParams(param1, param2));
    }

    clob(operation: string, clob: unknown, param1?: unknown, param2?: unknown) {
        StatLogger.clob(this.getStatOperation(operation), StatLogger.valueToStr(clob), ...StatLogger.getStatParams(param1, param2));
    }
}
